import { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Company-facing "Supporting documents" section for the DSQ page.
//
// Design intent (docs/29:163-165,:189; D8-amended): the company uploads
// documents DIRECTLY into the Drive dataroom's Company Documents/ folder via
// the uploadDealDocument callable, and can see the files it and its analyst
// have shared — but NEVER reaches Google Drive itself. So:
//   - upload sends folder:'company'. The server (uploadDealDocument) authorises
//     the caller and pins a company account to its own deal + the Company
//     folder regardless of what the client sends — this UI is convenience over
//     an already-enforced boundary (Decision 2), never the control itself.
//   - the list shows names + dates only. There is NO company-safe preview:
//     getDocumentPreviewUrl returns raw Drive links, which the access model
//     forbids exposing to a company. We deliberately do not call it. A
//     portal-mediated preview proxy is separate future work.

// Read a File as base64 with the data: prefix stripped — the exact encoding
// uploadDealDocument expects (mirrors DataroomPanel's internal caller).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(String(ev.target.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Could not read the file. Please try again.'));
    reader.readAsDataURL(file);
  });
}

// Map a callable failure to something a company user can act on. The one we
// must handle explicitly is the missing dataroom: until the auto-provisioning
// function is deployed, a deal can have no drive_folder_id and the server
// throws failed-precondition "Dataroom not created yet".
function friendlyUploadError(err) {
  const msg = String(err?.message || '');
  if (
    err?.code === 'functions/failed-precondition' ||
    /dataroom not created/i.test(msg)
  ) {
    return "Document uploads aren't ready for your deal yet — please contact your Mirepa analyst.";
  }
  if (err?.code === 'functions/permission-denied') {
    return 'You are not permitted to upload to this deal.';
  }
  return 'Upload failed. Please try again, or contact your Mirepa analyst if it keeps happening.';
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function CompanyDocuments({ dealId }) {
  const [documents, setDocuments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [message, setMessage] = useState('');

  // Stable across renders — getFunctions() returns a fresh handle each call, so
  // memoising it keeps the fetch effect from re-firing on every state change.
  const functions = useMemo(() => getFunctions(), []);

  const fetchDocuments = useCallback(async () => {
    if (!dealId) return;
    setListLoading(true);
    setListError('');
    try {
      const getDocs = httpsCallable(functions, 'getDealDocuments');
      const res = await getDocs({ dealId });
      setDocuments(res.data?.documents || []);
    } catch (err) {
      setListError('Could not load your documents just now. Please refresh the page.');
    } finally {
      setListLoading(false);
    }
  }, [functions, dealId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later by clearing the input value.
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setUploadError('');
    setMessage('');
    try {
      const fileBase64 = await fileToBase64(file);
      const upload = httpsCallable(functions, 'uploadDealDocument');
      await upload({
        dealId,
        fileName: file.name,
        fileBase64,
        mimeType: file.type,
        // The company can only ever write to its own Company folder; the server
        // enforces this regardless. Sent explicitly for wire-compatibility.
        folder: 'company',
        visibility: 'shared',
      });
      setMessage(`"${file.name}" uploaded.`);
      await fetchDocuments();
    } catch (err) {
      setUploadError(friendlyUploadError(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-header">
        <h2>Supporting documents</h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Upload documents to support your questionnaire. Your files are held securely
          and shared with your Mirepa analyst — no one outside your deal can see them.
        </p>
      </div>

      <div className="card-body">
        {uploadError && <div className="alert alert-error">{uploadError}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div style={{ marginBottom: 16 }}>
          <label className={`btn btn-primary ${uploading ? 'btn-disabled' : ''}`}
            style={{ cursor: uploading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {uploading && <span className="spinner spinner-sm" />}
            {uploading ? 'Uploading…' : 'Upload a document'}
            <input
              type="file"
              aria-label="Upload a document"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={handleFileChange}
            />
          </label>
        </div>

        {listLoading ? (
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Loading your documents…</p>
        ) : listError ? (
          <div className="alert alert-error">{listError}</div>
        ) : documents.length === 0 ? (
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
            No documents yet.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {documents.map((doc) => (
              <li key={doc.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid var(--color-border-light)',
                }}>
                <span style={{ fontSize: 14, wordBreak: 'break-word' }}>
                  📄 {doc.file_name || 'Untitled document'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {formatWhen(doc.uploaded_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
